<template>
  <section id="mainMapWrapper" class="main-map">
    <div
      id="panner"
      class="main-map__pan"
      :class="{ 'zoomed-in': zoomIn }"
      :style="{
        transformOrigin: `${this.dx}% ${this.dy}%`,
        transform: `scale(${this.scale})`
      }"
    >
      <div class="map-wrapper">
        <img
          @load="setPanDimensions"
          id="img"
          src="@/assets/images/aug-update/webp/map-img-complete-sm.webp"
          class="main-map-img no-flicker"
        />

        <img
          class="main-map-img--blur no-flicker"
          :class="{
            'blur-dark': productCards.length || isNotZoomedHighlight,
            'remove-transition-duration': removeTransitionDuration
          }"
          src="@/assets/images/aug-update/webp/map-img-complete-blur-sm.webp"
        />
        <img
          src="@/assets/images/aug-update/webp/map-img-skeleton-sm.webp"
          class="main-map-img--mask no-flicker"
          :class="{
            mask: isNotZoomedHighlight,
            'remove-transition-duration': removeTransitionDuration
          }"
          :style="{
            maskImage: `url(${getHighlightUrl(maskImg)})`,
            maskSize: `${maskSize.width}% ${maskSize.height}%`,
            maskPosition: `${maskPosition.x}% ${maskPosition.y}%`
          }"
        />

        <img
          v-show="isZoomed"
          class="main-map-img--blur no-flicker"
          :class="{
            blur: isZoomed,
            'remove-transition-duration': removeTransitionDuration
          }"
          src="@/assets/images/aug-update/webp/map-img-complete-blur.webp"
        />
        <img
          v-show="isZoomed"
          src="@/assets/images/aug-update/webp/map-img-skeleton-smaller.webp"
          class="main-map-img--mask no-flicker"
          :class="{
            mask: isZoomed,
            'remove-transition-duration': removeTransitionDuration
          }"
          :style="{
            maskImage: `url(${getHighlightUrl(maskImg)})`,
            maskSize: `${maskSize.width}% ${maskSize.height}%`,
            maskPosition: `${maskPosition.x}% ${maskPosition.y}%`
          }"
        />
        <div
          class="map-content-wrapper"
          :style="{ width: `${imgWidth}px`, height: `${imgHeight}px` }"
        >
          <img src="@/assets/images/title-text.png" alt class="title no-flicker" />
          <transition-group name="ambient">
            <AmbientHandler
              v-for="(animate, i) in ambientAnimations"
              v-show="showAmbientAnimations"
              ref="ambientAnimationHandler"
              :key="i + 'ambient'"
              :animation="animate"
              :data="animate"
            />
          </transition-group>
          <transition-group name="ambient">
            <micro-handler
              v-for="microAnimation in microAnimationsPlaying"
              :key="microAnimation.ref + 'Animation'"
              :microAnimation="microAnimation"
            />
          </transition-group>
          <transition name="ambient">
            <MacroHandler
              v-if="showMacroAnimation"
              ref="macroAnimationHandler"
              :onFinish="onFinish"
            />
          </transition>

          <transition
            v-for="(animationButton, i) in animationButtons"
            :key="i + animationButton.name.split(' ').join('-')"
            :name="
              showProductCard === false
                ? 'fade-out-' + animationButton.transition
                : animationButton.transition
            "
          >
            <animation-button
              v-if="
                showButtons ||
                  (currentProductId != null &&
                    currentProductId.toString() &&
                    productButtonFilterMap[currentProductId] &&
                    productButtonFilterMap[currentProductId].includes(
                      animationButton.parentNumId
                    ))
              "
              :i="i"
              :animationButton="animationButton"
              :ambientAnimationTimeout="ambientAnimationTimeout"
              :removeAnimationTimeout="removeAnimationTimeout"
              :findMask="findMask"
              :class="{ 'set-delay': delay }"
            />
          </transition>
          <!-- micro buttons go here -->
          <transition-group name="ambient">
            <micro-button
              v-for="microButton in microAnimations"
              v-if="showAmbientAnimations"
              :key="microButton.ref"
              :microButton="microButton"
            />
          </transition-group>

          <product-icon-reveal
            v-for="(productIcon, i) in productIcons"
            v-if="showIconsAnimation"
            ref="productIcons"
            :key="productIcon.icon + productIcon.parentId + i"
            :revealIcon="productIconsToReveal.includes(productIcon.parentId)"
            :productIcon="productIcon"
          />
          <transition name="intro">
            <intro-text-box
              v-if="introTextBox !== undefined"
              :onClose="onClose"
              :active="introTextBox !== undefined"
              :textBox="introTextBox"
              :panAndZoomTo="panAndZoomTo"
            />
          </transition>
          <transition name="pain">
            <pain-point-textBox
              v-if="painPointTextBox !== undefined"
              :onClose="onClose"
              :active="painPointTextBox !== undefined"
              :playMacroAnimation="playMacroAnimation"
              :textBox="painPointTextBox"
            />
          </transition>
          <transition v-for="(textBox, i) in textBoxes" :key="textBox.ref + i" name="textbox">
            <story-text-box
              v-if="stopTimeIdx === i"
              :ref="textBox.ref"
              :active="animationPlaying && stopTimeIdx === i"
              :onClose="onClose"
              :onFinish="onFinish"
              :textBox="textBox"
              :next="next"
              :lastTextBox="textBoxes.length - 1 === i"
              :previousProgress="
                textBoxes[i - 1] ? textBoxes[i - 1].stopTime : 0
              "
              :animationProgress="textBox.stopTime"
              :updated="animationPlaying && stopTimeIdx === i"
            />
          </transition>
          <transition name="pain">
            <technology-text-box
              v-if="technologyLandscapeTextBox !== undefined"
              :onClose="onClose"
              :active="technologyLandscapeTextBox !== undefined"
              :textBox="technologyLandscapeTextBox"
              :revealIconsOnTimeOut="revealIconsOnTimeOut"
              :clearIconRevealTimeoutsAndRevealAll="clearIconRevealTimeoutsAndRevealAll"
            />
          </transition>

          <div
            v-for="touchPoint in touchPoints"
            :id="touchPoint.ref"
            :key="touchPoint.ref"
            class="touch-point"
            :style="{
              top: touchPoint.position.top,
              left: touchPoint.position.left,
              width: touchPoint.size.width ? touchPoint.size.width : null,
              height: touchPoint.size.height ? touchPoint.size.height : null
            }"
          ></div>
        </div>
      </div>
    </div>
    <transition name="recap">
      <Benefit-text-box
        v-if="benefitCardTextBox !== undefined"
        :onClose="onCloseAnimationFinished"
        :active="benefitCardTextBox !== undefined"
        :textBox="benefitCardTextBox"
        :lastTextBox="true"
      />
    </transition>

    <header-bar :reset="reset" />

    <product-bar />
  </section>
</template>

<script>
//COMPONENTS
import MacroHandler from "./animation-components/LottieMacroAniHandler";
import MicroHandler from "./animation-components/LottieMicroAniHandler";
import AmbientHandler from "./animation-components/LottieAmbientAniHandler";
import IntroHandler from "./animation-components/LottieIntroAniHandler";
import AnimationButton from "./AnimationButton";
import MicroButton from "./MicroAnimationButton";
import ProductBar from "./ProductBar";
import HeaderBar from "./HeaderBar";
import ProductIconReveal from "./ProductIconReveal";
import IntroTextBox from "./IntroTextBox";
import PainPointTextBox from "./PainPointTextBox";
import StoryTextBox from "./StoryTextBox";
import TechnologyTextBox from "./TechnologyTextBox";
import BenefitTextBox from "./BenefitTextBox";
import IndustryBenefitTextBox from "./IndustryBenefitTextBox";

import logoSetData from "@/assets/json-data/logoSetData";

//Plugins
import { mapState, mapMutations, mapActions } from "vuex";
export default {
  name: "main-map",
  components: {
    MacroHandler,
    AmbientHandler,
    IntroTextBox,
    StoryTextBox,
    AnimationButton,
    ProductBar,
    HeaderBar,
    ProductIconReveal,
    PainPointTextBox,
    TechnologyTextBox,
    BenefitTextBox,
    IntroHandler,
    IndustryBenefitTextBox,
    MicroButton,
    MicroHandler
  },
  props: [],
  data() {
    return {
      logos: {},
      el: "",
      zoomIn: false,
      maskImg: "circle.svg",
      maskSize: { width: 0, height: 0 },
      maskPosition: { x: 0, y: 0 },
      transitionEventListener: "",
      playTimeout: undefined,
      closeTimeout: undefined,
      parentEl: undefined,
      showIconsAnimation: false,
      ambientAnimationTimeout: null,
      removeAnimationTimeout: null,
      imgHeight: 0,
      imgWidth: 0,
      iconsToRevealTimeouts: []
    };
  },

  created() {
    this.setProducts();
    this.setMicroAnimations();
    this.microAnimationButtonGenerator();
    this.generateMacroAnimationNumId();
    this.generateAmbientAnimation();
    this.selectTouchPointSet();
    this.generateMasks();
    this.generateAnimationButtons();
    this.createProductIcons();
    this.createProductButtonFilterMap();
    this.createAnimationProductFilterMap();
    this.createProductsDelays();
    this.logos = logoSetData();
    this.updateSpecificScale(1);
  },
  mounted() {
    this.updateIsZoomed(false);
    window.addEventListener("resize", this.setPanDimensions);
    window.addEventListener("resize", this.selectTouchPointSet);
  },
  computed: {
    ...mapState({
      microAnimationsPlaying: state =>
        state.moduleMicroAnimations.microAnimationsPlaying,
      microAnimations: state => state.microAnimations,
      animationFrame: state => state.moduleMacroAnimations.animationFrame,
      ambientAnimations: state => state.ambientAnimations,
      showMacroAnimation: state =>
        state.moduleMacroAnimations.showMacroAnimation,
      showAmbientAnimations: state =>
        state.moduleAmbientAnimations.showAmbientAnimations,
      macroAnimation: state => state.moduleMacroAnimations.macroAnimation,
      animationPlaying: state => state.moduleMacroAnimations.animationPlaying,
      touchPoints: state => state.objBuild.touchPoints,
      masks: state => state.objBuild.masks,
      textBoxes: state => state.textBox.textBoxes,
      stopTimeIdx: state => state.textBox.stopTimeIdx,
      animationButtons: state => state.textBox.animationButtons,
      introTextBox: state => state.textBox.introTextBox,
      painPointTextBox: state => state.textBox.painPointTextBox,
      technologyLandscapeTextBox: state =>
        state.textBox.technologyLandscapeTextBox,
      benefitCardTextBox: state => state.textBox.benefitCardTextBox,
      dx: state => state.panZoom.dx,
      dy: state => state.panZoom.dy,
      scale: state => state.panZoom.scale,
      isZoomed: state => state.panZoom.isZoomed,
      isNotZoomedHighlight: state => state.panZoom.isNotZoomedHighlight,
      removeTransitionDuration: state => state.panZoom.removeTransitionDuration,
      goToPoint: state => state.panZoom.goToPoint,
      productCards: state => state.productBar.productCards,
      productIconsToReveal: state => state.productBar.productIconsToReveal,
      productIcons: state => state.productBar.productIcons,
      animationProductFilterMap: state =>
        state.filter.animationProductFilterMap,
      showProductCard: state => state.productBar.showProductCard,
      showButtons: state => state.textBox.showButtons,
      currentProductId: state => state.productBar.currentProductId,
      delay: state => state.textBox.delay,
      productButtonFilterMap: state => state.filter.productButtonFilterMap,
      showStaticLogo: state => state.logos.showStaticLogo,
      currentAnimationName: state => state.analytics.currentAnimationName
    })
  },
  watch: {
    goToPoint: function(val) {
      this.zoomIn = true;
      this.getPositionDifference("mainMapWrapper", val);
    }
  },
  methods: {
    ...mapMutations([
      "updateShowButtons",
      "setMicroAnimations",
      "setProducts",
      "clearMacroAnimation",
      "updateAnimationPlaying",
      "updateStopTimeIdx",
      "updateDxDy",
      "updateScale",
      "updateIsZoomed",
      "setIntroTextbox",
      "updateIsNotZoomedHighlight",
      "setDelay",
      "updateShowMacroAnimation",
      "setProductCards",
      "updateRaiseIcon",
      "setProductIconsToReveal",
      "updateShowAmbientAnimations",
      "updateProductMotion",
      "updateShowStaticLogo",
      "updateCurrentAnimation",
      "updateRemoveTransitionDuration",
      "updateSpecificScale"
    ]),
    ...mapActions([
      "generateAnimationTextBoxes",
      "microAnimationButtonGenerator",
      "generateTouchPoints",
      "generateMasks",
      "findMacroAnimation",
      "generateAnimationButtons",
      "createProductIcons",
      "getPainPointTextBox",
      "getTechnologyLandscapeTextBox",
      "generateMacroAnimationNumId",
      "generateAmbientAnimation",
      "createProductButtonFilterMap",
      "createAnimationProductFilterMap",
      "findProductIconsToReveal",
      "removeProductIconsToReveal",
      "removeProductIconsActive",
      "createProductsDelays"
    ]),
    setPanDimensions() {
      this.imgHeight = document.getElementById("img").offsetHeight;
      this.imgWidth = document.getElementById("img").offsetWidth;
    },
    selectTouchPointSet() {
      let isFiveByOne = window.innerWidth / window.innerHeight > 256 / 135;
      this.generateTouchPoints(isFiveByOne ? "fiveByOne" : "sixteenByNine");
    },
    panAndZoomTo(parentId, id) {
      this.setIntroTextbox(undefined);

      this.zoomIn = false;
      clearTimeout(this.closeTimeout);
      let parentEl = document.getElementById(parentId);
      this.getPositionDifference(parentId, id)
        .then(res => {
          this.updateScale("maxScale");
        })
        .then(() => {
          this.getPainPointTextBox();
          this.updateRemoveTransitionDuration(true);
        });
    },
    getPositionDifference(parentId, id) {
      let parentEl = document.getElementById(parentId);
      return new Promise((resolve, reject) => {
        let el = document.getElementById(id);
        if (el) {
          let left = el.offsetLeft;
          let top = el.offsetTop;
          let width = el.offsetWidth;
          let height = el.offsetHeight;
          let cx = left;
          let cy = top;
          let dx = (cx / parentEl.clientWidth) * 100;
          let dy = (cy / parentEl.clientHeight) * 100;
          this.updateDxDy({ dx, dy });
          resolve();
        }
      });
    },
    findMask(id) {
      return new Promise((resolve, reject) => {
        var maskFound = this.masks.find(element => {
          return element.ref === id;
        });
        this.maskImg = maskFound.img;
        this.maskSize = maskFound.size;
        this.maskPosition = maskFound.position;
        resolve();
      });
    },
    playMacroAnimation(id) {
      this.generateAnimationTextBoxes().then(() => {
        setTimeout(() => {
          this.updateAnimationPlaying(true);
        }, 2000);

        this.$refs.macroAnimationHandler.play();
      });
    },
    next() {
      this.zoomIn = false;
      this.$refs.macroAnimationHandler.skipAnimationSection();
    },
    reset() {
      this.updateShowButtons(true);
      this.updateIsNotZoomedHighlight(false);
    },
    onClose() {
      this.setProductCards([]);
      this.updateShowButtons(true);
      this.updateShowMacroAnimation(false);
      this.updateIsZoomed(false);
      this.updateIsNotZoomedHighlight(false);
      clearTimeout(this.playTimeout);
      this.updateAnimationPlaying(false);
      this.updateStopTimeIdx(-1);
      this.$refs.macroAnimationHandler.stop();
      this.clearMacroAnimation();
      this.fadeProductIcons();
      this.closeTimeout = setTimeout(() => {
        this.updateScale("minScale");
      }, 1000);
    },
    onFinish() {
      clearTimeout(this.playTimeout);
      this.setProductCards([]);
      this.$refs.macroAnimationHandler.stop();
      this.updateStopTimeIdx(-1);
      this.getTechnologyLandscapeTextBox();
    },
    onCloseAnimationFinished(isClosed) {
      if (isClosed) {
        //timeout to remove product icons from map after experience
        this.updateIsNotZoomedHighlight(true);
        setTimeout(() => {
          this.fadeProductIcons();
        }, 5000);
      } else {
        this.updateIsNotZoomedHighlight(false);
        this.updateRemoveTransitionDuration(false);
      }
      this.setProductCards([]);
      this.updateIsZoomed(false);
      this.updateAnimationPlaying(false);
      this.updateStopTimeIdx(-1);
      this.closeTimeout = setTimeout(() => {
        this.updateScale("minScale");
      }, 1000);
      this.setDelay(false);
    },
    revealIconsOnTimeOut() {
      this.updateRaiseIcon(true);
      this.showIconsAnimation = true;
      this.setProductCards([]);
      this.updateStopTimeIdx(-1);
      let iconRevealDelayTimeout = setTimeout(() => {
        for (
          let i = 0;
          i < this.animationProductFilterMap[this.macroAnimation.numId].length;
          i++
        ) {
          let iconRevealSetTimeout = setTimeout(() => {
            if (this.animationProductFilterMap[this.macroAnimation.numId]) {
              this.findProductIconsToReveal(
                this.animationProductFilterMap[this.macroAnimation.numId][i]
              );
            } else {
              return;
            }
          }, i * 1000);
          this.iconsToRevealTimeouts.push(iconRevealSetTimeout);
        }
      }, 3000);
      this.iconsToRevealTimeouts.push(iconRevealDelayTimeout);
    },
    clearIconRevealTimeoutsAndRevealAll() {
      for (let iconTimeout of this.iconsToRevealTimeouts) {
        window.clearTimeout(iconTimeout);
      }
      this.setProductIconsToReveal(
        this.animationProductFilterMap[this.macroAnimation.numId]
      );
    },
    getImg(pic) {
      if (pic) {
        var image = require.context("@/assets/logos/", false);
        return image("./" + pic);
      }
    },
    getHighlightUrl(pic) {
      if (pic) {
        var image = require.context("@/assets/images/highlights/", false);
        return image("./" + pic);
      }
    },
    fadeProductIcons() {
      this.updateRemoveTransitionDuration(false);
      this.updateCurrentAnimation("");
      this.updateRaiseIcon(false);
      this.updateIsNotZoomedHighlight(false);
      this.setProductIconsToReveal([]);
      this.removeProductIconsActive();
      this.updateShowButtons(true);
      this.removeAnimationTimeout = setTimeout(() => {
        this.updateShowMacroAnimation(false);
      }, 1000);
      this.ambientAnimationTimeout = setTimeout(() => {
        this.showIconsAnimation = false;
        this.updateShowAmbientAnimations(true);
      }, 1800);
    }
  }
};
</script>

<style scoped lang="scss">
@import "../styles/_vars";
.intro-animation-top-bar {
  position: fixed;
  top: 0;
  width: 100vw;
  height: 4.5vw;
  transform: rotate(180deg);
}
.intro-animation-middle {
  position: fixed;
  top: 0;
  width: 100vw;
  height: 100vh;
}
.intro-animation-bottom-bar {
  position: fixed;
  bottom: 0;
  width: 100vw;
  height: 4.5vw;
}
.main-map {
  position: relative;
  width: 100vw;
  height: 100vh;

  &__pan {
    position: relative;
    width: 100vw;
    height: 100vh;
    transition: transform $panZoomDuration ease-in-out;
  }
}
.zoomed-in {
  transition: transform $panZoomDuration ease-in-out,
    transform-origin $panZoomDuration ease-in-out;
}
.test-close {
  cursor: pointer;
  position: fixed;
  z-index: 100;
}
.main-map-img {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: auto;
  transition: opacity $transition-duration ease-in-out;
}
.main-map-img--blur {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity $transition-duration ease-in-out,
    filter $transition-duration ease-in-out;
}
.main-map-img--mask {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: auto;
  opacity: 0;
  mask-repeat: no-repeat;
  transition: opacity $transition-duration ease-in-out;
}

.mask {
  opacity: 1;
}
.blur {
  filter: brightness(30%);
  opacity: 1;
}
.blur-dark {
  opacity: 1;
  filter: brightness(30%);
}
.map-content-wrapper {
  position: absolute;
  top: 50%;
  width: 100vw;
  height: 37vw;
  transform: translate3d(0px, -50%, 1px);
}
.touch-point {
  position: absolute;
  display: block;
  z-index: -1;
}
.intro-enter-active {
  transition: opacity $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97),
    transform $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
}
.intro-enter {
  opacity: 0;
  transform: scale(1);
}
.intro-leave-active {
  transition: opacity $button-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $button-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.intro-leave-to {
  opacity: 0;
  transform: scale(1);
}
.pain-enter-active {
  transition: opacity $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
  transition-delay: 2s;
}
.pain-enter {
  opacity: 0;
}
.pain-leave-active {
  transition: opacity $button-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.pain-leave-to {
  opacity: 0;
}
.ambient-enter-active {
  transition: opacity $transition-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
}
.ambient-enter {
  opacity: 0;
}
.ambient-leave-active {
  transition: opacity $transition-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.ambient-leave-to {
  opacity: 0;
}

.textbox-enter-active {
  transition: opacity $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97),
    transform $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
}
.textbox-enter {
  opacity: 0;
  transform: translate(0%, 10%);
}
.textbox-leave-active {
  transition: opacity $button-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $button-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.textbox-leave-to {
  opacity: 0;
  transform: translate(0%, 10%);
}
.recap-enter-active {
  transition: opacity $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97),
    transform $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
}
.recap-enter {
  opacity: 0;
  transform: translate(-50%, 10%);
}
.recap-leave-active {
  transition: opacity $button-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $button-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.recap-leave-to {
  opacity: 0;
  transform: translate(-50%, 10%);
}
.fade-out-left-enter-active {
  transition: opacity $button-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $button-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.fade-out-left-enter {
  opacity: 0;
  transform: translateX(-400px);
}
.fade-out-left-leave-active {
  transition: opacity $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97),
    transform $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
}
.fade-out-left-leave-to {
  opacity: 0;
  transform: translateX(-400px);
}

.fade-out-right-enter-active {
  transition: opacity $button-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $button-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.fade-out-right-enter {
  opacity: 0;
  transform: translateX(400px);
}
.fade-out-right-leave-active {
  transition: opacity $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97),
    transform $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
}
.fade-out-right-leave-to {
  opacity: 0;
  transform: translateX(400px);
}

.fade-in-logo-enter-active {
  transition: opacity $button-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $button-duration cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: 2s;
}
.fade-in-logo-enter {
  opacity: 0;
  transform: translateX(400px);
}
.fade-in-logo-leave-active {
  transition: opacity $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97),
    transform $button-duration cubic-bezier(0.64, 0.03, 0.07, 0.97);
  transition-delay: 0.7s;
}
.fade-in-logo-leave-to {
  opacity: 0;
  transform: translateX(400px);
}

.left-enter-active {
  transition: opacity $transition-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $transition-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.left-enter {
  opacity: 0;
  transform: translateX(-200px);
}
.left-leave-active {
  transition: opacity $transition-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $transition-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.left-leave-to {
  opacity: 0;
  transform: translateX(-200px);
}

.right-enter-active {
  transition: opacity $transition-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $transition-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.right-enter {
  opacity: 0;
  transform: translateX(200px);
}
.right-leave-active {
  transition: opacity $transition-duration cubic-bezier(0.22, 1, 0.36, 1),
    transform $transition-duration cubic-bezier(0.22, 1, 0.36, 1);
}
.right-leave-to {
  opacity: 0;
  transform: translateX(200px);
}

.set-delay {
  transition-delay: 1.5s !important;
}

.static-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.lower-left {
  position: absolute;
  right: 2.6vw;
  bottom: 6vw;
}
.title {
  position: absolute;
  top: 33%;
  left: 1%;
  width: 25vw;
  @media screen and (max-aspect-ratio: 256/135) {
    top: 32%;
    left: 5%;
    width: 32vw;
  }
}
.remove-transition-duration {
  transition: opacity 0s ease-in-out, filter 0s ease-in-out;
}
</style>
